<script lang="ts">
	import { getClients } from "@api/getClients.js";
	import getMemberPostContentQuery from "@api/queries/getMemberPostContent.graphql";
	import ArticleCard from "@components/ArticleCard.svelte";
	import Logo from "@components/Logo.svelte";
	import PostPublishedDate from "@components/PostPublishedDate.svelte";
	import Button from "@components/common/Button.svelte";
	import { ButtonSize } from "@components/common/ButtonSize.js";
	import CkEditorContent from "@components/common/CkEditorContent.svelte";
	import ContentBlocks from "@components/common/ContentBlocks.svelte";
	import Heading from "@components/common/Heading.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import Media from "@components/common/Media.svelte";
	import StructuredData from "@components/common/StructuredData.svelte";
	import Layout from "@components/layout/Layout.svelte";
	import * as Sentry from "@sentry/browser";
	import { Auth } from "@utils/Auth.svelte.js";
	import { getTranslate } from "@utils/getTranslate.js";
	import type { BlogPosting } from "schema-dts";
	import loaderIcon from "@core/assets/icons/loader.svg?raw";
	import { ButtonVariant } from "@core/schema/ButtonVariant.js";
	import type { SavedContentBlock } from "@core/schema/ContentBlock.js";
	import { getSubscriptionPagePath } from "@core/schema/paths/getSubscriptionPagePath.js";
	import { createPromiseWithStatus } from "@core/utils/svelte/createPromiseWithStatus.svelte.js";
	import { oneTimeEffect } from "@core/utils/svelte/oneTimeEffect.svelte.js";
	import { thumbUploadedFile } from "@core/utils/thumbUploadedFile.js";
	import { getGlobalSettings } from "../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { SeoFragment } from "../seo/SeoFragment.js";
	import { idRef, seoId } from "../seo/seoIds.js";
	import translations from "./PostDetail.translations.json" with { type: "json" };
	import type { PostDetailProps } from "./PostDetailProps.js";

	const { post, relatedPosts }: PostDetailProps = $props();
	const settings = getGlobalSettings();
	const { WEB_URL_OR_LOCAL, FILE_BUCKET_URL_MAP } = settings;
	const { tenant, pagePath } = getGlobalTemplateProps();

	const translate = getTranslate(translations);

	async function loadMemberContent(): Promise<SavedContentBlock[]> {
		const { graphQL } = await getClients(settings);
		const content = await graphQL.query<SavedContentBlock[], { id: string }>({
			query: getMemberPostContentQuery,
			variables: { id: post.id },
		});
		return content ?? [];
	}

	const memberContent = createPromiseWithStatus(loadMemberContent, undefined, (error: unknown) => {
		Sentry.captureException(error);
	});

	oneTimeEffect(() => post.ffcMembersOnly && Auth.loaded && Auth.isFfcMember, memberContent.call);

	type MemberContentPhase = "content" | "error" | "loading" | "locked";
	const memberContentPhase = $derived.by<MemberContentPhase>(() => {
		if (!Auth.loaded) {
			return "loading";
		}
		if (!Auth.isFfcMember) {
			return "locked";
		}
		if (memberContent.value.status === "success") {
			return "content";
		}
		if (memberContent.value.status === "error") {
			return "error";
		}
		return "loading";
	});

	const canonicalUrl = $derived(
		tenant.locale in pagePath ? `${WEB_URL_OR_LOCAL}${pagePath[tenant.locale]}` : undefined,
	);

	const imageUrl = $derived(
		post.primaryImage !== undefined ? thumbUploadedFile(1200, 630, post.primaryImage, FILE_BUCKET_URL_MAP) : undefined,
	);

	const organizationRef = $derived(idRef(seoId(WEB_URL_OR_LOCAL, SeoFragment.Organization)));

	const articleSchema = $derived<BlogPosting>({
		"@type": "BlogPosting",
		headline: post.title,
		description: post.perex,
		...(imageUrl !== undefined ? { image: imageUrl } : {}),
		datePublished: post.publishedAt,
		dateModified: post.modifiedAt,
		...(canonicalUrl !== undefined ? { mainEntityOfPage: canonicalUrl } : {}),
		author: organizationRef,
		publisher: organizationRef,
		...(post.ffcMembersOnly
			? {
					isAccessibleForFree: false,
					hasPart: [
						{
							"@type": "WebPageElement",
							isAccessibleForFree: false,
							cssSelector: ".ffc-only-content",
						},
					],
				}
			: { isAccessibleForFree: true }),
	});
</script>

<StructuredData data={articleSchema} />

<Layout ogImage={post.primaryImage}>
	<div class="container-8 mb-14 md:mb-30 mt-6 md:mt-8">
		<PostPublishedDate {post} />
		<Heading
			level={1}
			text={post.title}
			class={["leading-tighter text-3.5xl font-antipol text-fcrdarkgray my-4", "md:text-4.5xl md:mt-6 md:mb-12"]}
		/>
		<CkEditorContent
			unstyled
			class="text-fcrbrown text-base font-antipol md:text-1.5xl/normal [&_a]:underline"
			html={post.perex}
		/>
	</div>
	<div class="full-width relative">
		{#if post.primaryImage}
			<Media
				alt={post.primaryImage.title ?? ""}
				source={post.primaryImage}
				width={343}
				height={320}
				sourcesets={{
					"<sm": [0, 343],
					">sm": [0, 960],
					">md": [0, 1280],
					">lg": [0, 1600],
					">xl": [0, 1920],
					">2xl": [0, 2400],
					">3xl": [0, 0],
				}}
				wrapperClass="overflow-hidden aspect-[5/3] md:aspect-[21/9]"
				class="object-cover size-full bg-fcrdarkbrown"
			/>
		{:else}
			<div class="aspect-[5/3] md:aspect-[21/9] bg-fcrdarkbrown flex justify-center items-center">
				<Logo class="text-white w-1/3" />
			</div>
		{/if}
		{#if post.ffcMembersOnly && memberContentPhase !== "content"}
			<div
				class="pointer-events-none absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-b from-transparent to-white"
			></div>
		{/if}
	</div>
	{#if post.ffcMembersOnly}
		{#if memberContentPhase === "content" && memberContent.value.status === "success"}
			<ContentBlocks class="ffc-only-content pt-4 md:pt-30 pb-14 md:pb-40" blocks={memberContent.value.result} />
		{:else if memberContentPhase === "error"}
			<div
				class="ffc-only-content container-8 flex flex-col items-center gap-6 pt-4 pb-14 text-center md:pt-14 md:pb-40"
			>
				<Heading
					level={2}
					text={translate("ffcContentErrorHeading")}
					class="font-antipol text-2xl text-fcrdarkgray md:text-3xl"
				/>
				<CkEditorContent
					unstyled
					class="text-fcrbrown max-w-xl text-base font-antipol"
					html={translate("ffcContentErrorDescription")}
				/>
				<Button
					variant={ButtonVariant.Outlined}
					size={ButtonSize.Medium}
					loading={memberContent.value.status === "loading"}
					onclick={memberContent.call}
				>
					{translate("ffcContentRetry")}
				</Button>
			</div>
		{:else if memberContentPhase === "loading"}
			<div
				class="ffc-only-content container-8 flex flex-col items-center gap-4 pt-4 pb-14 text-center md:pt-14 md:pb-40"
			>
				<Icon icon={loaderIcon} class="size-8 animate-[spin_1s_steps(8,_jump-end)_infinite] text-fcrbrown" />
				<CkEditorContent
					unstyled
					class="text-fcrbrown max-w-xl text-base font-antipol"
					html={translate("ffcContentLoading")}
				/>
			</div>
		{:else}
			<div
				class="ffc-only-content container-8 flex flex-col items-center gap-6 pt-4 pb-14 text-center md:pt-14 md:pb-40"
			>
				<Heading
					level={2}
					text={translate("ffcOnlyHeading")}
					class="font-antipol text-2xl text-fcrdarkgray md:text-3xl"
				/>
				<CkEditorContent
					unstyled
					class="text-fcrbrown max-w-xl text-base font-antipol"
					html={translate("ffcOnlyDescription")}
				/>
				<Link href={getSubscriptionPagePath(tenant)} variant={ButtonVariant.Dark} size={ButtonSize.Medium}>
					{translate("ffcOnlyCta")}
				</Link>
			</div>
		{/if}
	{:else}
		<ContentBlocks class="pt-4 md:pt-30 pb-14 md:pb-40" blocks={post.content} />
	{/if}
	{#if relatedPosts.length > 0}
		<section class="full-width grid-container py-14 md:py-30 bg-fcrbeige">
			<Heading
				level={2}
				class="underline text-fcrdarkgray font-antipol text-3.5xl/normal pb-9 md:pb-18"
				text={translate("moreArticles")}
			/>
			<section class="grid md:grid-cols-2 xl:grid-cols-3 gap-x-4 gap-y-14 md:gap-y-18 w-fit mx-auto">
				{#each relatedPosts as post (post.id)}
					<ArticleCard headingLevel={3} {post} huge={false} />
				{/each}
			</section>
		</section>
	{/if}
</Layout>
